// Toaster.tsx
import React, {
  useState,
  useEffect,
  useCallback,
  createContext,
  useContext,
} from "react";
import "../css/toaster.css";
import {
  ToasterConfig,
  ToasterContainerProps,
  ToasterItemProps,
  ToasterPlacement,
} from "../interfaces";

// Componente individual de toaster
const ToasterItem: React.FC<ToasterItemProps> = ({
  id,
  type,
  title,
  message,
  duration,
  closable,
  placement,
  onClose,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    // Animación de entrada
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration]);

  const handleClose = useCallback(() => {
    setIsLeaving(true);
    setTimeout(() => {
      onClose(id);
    }, 300); // Duración de la animación de salida
  }, [id, onClose]);

  const getIcon = () => {
    switch (type) {
      case "success":
        return "✓";
      case "error":
        return "✕";
      case "warning":
        return "⚠";
      case "info":
        return "ℹ";
      default:
        return "ℹ";
    }
  };

  const getAnimationClass = () => {
    const isLeft = placement.includes("Left");
    const isRight = placement.includes("Right");
    const isTop = placement.includes("top") || placement === "top";
    const isBottom = placement.includes("bottom") || placement === "bottom";

    let animationClass = "toast-anim-";

    if (isLeft) animationClass += "left";
    else if (isRight) animationClass += "right";
    else if (isTop) animationClass += "top";
    else if (isBottom) animationClass += "bottom";
    else animationClass += "right"; // default

    return animationClass;
  };

  return (
    <div
      className={`toast-card toast-${type} ${getAnimationClass()} ${
        isVisible ? "toast-show" : ""
      } ${isLeaving ? "toast-exit" : ""}`}
    >
      <div className="toast-inner">
        <div className="toast-symbol">{getIcon()}</div>
        <div className="toast-text">
          {title && <div className="toast-heading">{title}</div>}
          <div className="toast-content">{message}</div>
        </div>
        {closable && (
          <button
            className="toast-dismiss"
            onClick={handleClose}
            aria-label="Cerrar toaster"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
};

// Componente contenedor de toasters

const ToasterContainer: React.FC<ToasterContainerProps> = ({
  toasters,
  onRemove,
  defaultPlacement,
}) => {
  // Agrupar toasters por placement
  const groupedToasters = toasters.reduce((acc, toaster) => {
    const toasterPlacement = toaster.placement || defaultPlacement;
    if (!acc[toasterPlacement]) {
      acc[toasterPlacement] = [];
    }
    acc[toasterPlacement].push(toaster);
    return acc;
  }, {} as Record<ToasterPlacement, Array<Required<ToasterConfig>>>);

  if (toasters.length === 0) return null;

  return (
    <>
      {Object.entries(groupedToasters).map(([pos, toasterList]) => (
        <div key={pos} className={`toast-wrapper toast-wrapper-${pos}`}>
          {toasterList.map((toaster) => (
            <ToasterItem key={toaster.id} {...toaster} onClose={onRemove} />
          ))}
        </div>
      ))}
    </>
  );
};

// Context para los toasters
interface ToasterContextType {
  success: (config: Omit<ToasterConfig, "type">) => string;
  error: (config: Omit<ToasterConfig, "type">) => string;
  warning: (config: Omit<ToasterConfig, "type">) => string;
  info: (config: Omit<ToasterConfig, "type">) => string;
  open: (config: ToasterConfig) => string;
  config: (options: { placement?: ToasterPlacement }) => void;
  clearAll: () => void;
}

const ToasterContext = createContext<ToasterContextType | null>(null);

// Hook para usar los toasters
export const useToaster = () => {
  const context = useContext(ToasterContext);
  if (!context) {
    throw new Error("useToaster must be used within a ToasterProvider");
  }
  return context;
};

// Provider de toasters
interface ToasterProviderProps {
  children: React.ReactNode;
  defaultPlacement?: ToasterPlacement;
}

export const ToasterProvider: React.FC<ToasterProviderProps> = ({
  children,
  defaultPlacement = "topRight",
}) => {
  const [toasters, setToasters] = useState<Array<Required<ToasterConfig>>>([]);
  const [currentPlacement, setCurrentPlacement] =
    useState<ToasterPlacement>(defaultPlacement);

  const addToaster = useCallback(
    (config: ToasterConfig) => {
      const id =
        config.id ||
        `toaster-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const newToaster: Required<ToasterConfig> = {
        id,
        type: config.type || "info",
        title: config.title || "",
        message: config.message,
        duration: config.duration !== undefined ? config.duration : 4500,
        closable: config.closable !== undefined ? config.closable : true,
        placement: config.placement || currentPlacement,
        onClose: config.onClose || (() => {}),
      };

      setToasters((prev) => [...prev, newToaster]);
      return id;
    },
    [currentPlacement]
  );

  const removeToaster = useCallback((id: string) => {
    setToasters((prev) => {
      const toaster = prev.find((n) => n.id === id);
      if (toaster?.onClose) {
        toaster.onClose();
      }
      return prev.filter((n) => n.id !== id);
    });
  }, []);

  const clearAll = useCallback(() => {
    setToasters([]);
  }, []);

  const configToaster = useCallback(
    (options: { placement?: ToasterPlacement }) => {
      if (options.placement) {
        setCurrentPlacement(options.placement);
      }
    },
    []
  );

  const success = useCallback(
    (config: Omit<ToasterConfig, "type">) => {
      return addToaster({ ...config, type: "success" });
    },
    [addToaster]
  );

  const error = useCallback(
    (config: Omit<ToasterConfig, "type">) => {
      return addToaster({ ...config, type: "error" });
    },
    [addToaster]
  );

  const warning = useCallback(
    (config: Omit<ToasterConfig, "type">) => {
      return addToaster({ ...config, type: "warning" });
    },
    [addToaster]
  );

  const info = useCallback(
    (config: Omit<ToasterConfig, "type">) => {
      return addToaster({ ...config, type: "info" });
    },
    [addToaster]
  );

  const contextValue: ToasterContextType = {
    success,
    error,
    warning,
    info,
    open: addToaster,
    config: configToaster,
    clearAll,
  };

  return (
    <ToasterContext.Provider value={contextValue}>
      {children}
      <ToasterContainer
        toasters={toasters}
        onRemove={removeToaster}
        defaultPlacement={currentPlacement}
      />
    </ToasterContext.Provider>
  );
};

// API estática global (opcional, para compatibilidad con antd)
class ToasterApi {
  private static instance: ToasterApi;
  private context: ToasterContextType | null = null;

  static getInstance(): ToasterApi {
    if (!ToasterApi.instance) {
      ToasterApi.instance = new ToasterApi();
    }
    return ToasterApi.instance;
  }

  setContext(context: ToasterContextType) {
    this.context = context;
  }

  success(config: Omit<ToasterConfig, "type">) {
    if (!this.context) {
      console.warn(
        "ToasterProvider not found. Make sure to wrap your app with ToasterProvider."
      );
      return "";
    }
    return this.context.success(config);
  }

  error(config: Omit<ToasterConfig, "type">) {
    if (!this.context) {
      console.warn(
        "ToasterProvider not found. Make sure to wrap your app with ToasterProvider."
      );
      return "";
    }
    return this.context.error(config);
  }

  warning(config: Omit<ToasterConfig, "type">) {
    if (!this.context) {
      console.warn(
        "ToasterProvider not found. Make sure to wrap your app with ToasterProvider."
      );
      return "";
    }
    return this.context.warning(config);
  }

  info(config: Omit<ToasterConfig, "type">) {
    if (!this.context) {
      console.warn(
        "ToasterProvider not found. Make sure to wrap your app with ToasterProvider."
      );
      return "";
    }
    return this.context.info(config);
  }

  open(config: ToasterConfig) {
    if (!this.context) {
      console.warn(
        "ToasterProvider not found. Make sure to wrap your app with ToasterProvider."
      );
      return "";
    }
    return this.context.open(config);
  }

  config(options: { placement?: ToasterPlacement }) {
    if (!this.context) {
      console.warn(
        "ToasterProvider not found. Make sure to wrap your app with ToasterProvider."
      );
      return;
    }
    this.context.config(options);
  }
}

export const toaster = ToasterApi.getInstance();

// Hook para conectar la API estática con el Provider
export const useToasterApi = () => {
  const context = useToaster();

  useEffect(() => {
    toaster.setContext(context);
  }, [context]);

  return context;
};

// Ejemplo de uso
export const ToasterExample: React.FC = () => {
  const toaster = useToaster();

  const showSuccess = () => {
    toaster.success({
      title: "Éxito",
      message: "La operación se completó correctamente",
      duration: 3000,
      placement: "topRight",
    });
  };

  const showError = () => {
    toaster.error({
      title: "Error",
      message: "Hubo un problema al procesar la solicitud",
      duration: 5000,
      placement: "topLeft",
    });
  };

  const showWarning = () => {
    toaster.warning({
      title: "Advertencia",
      message: "Esta acción puede tener consecuencias",
      duration: 0,
      closable: true,
      placement: "bottomRight",
    });
  };

  const showInfo = () => {
    toaster.info({
      message: "Información importante para el usuario",
      placement: "bottomLeft",
    });
  };

  const showTop = () => {
    toaster.info({
      message: "Toaster centrado arriba",
      placement: "top",
    });
  };

  const showBottom = () => {
    toaster.success({
      message: "Toaster centrado abajo",
      placement: "bottom",
    });
  };

  const changePlacement = () => {
    toaster.config({ placement: "topLeft" });
    toaster.info({
      message: "Configuración cambiada a topLeft",
    });
  };

  return (
    <div style={{ padding: "20px" }}>
      <h2>Toaster Component Demo</h2>
      <div
        style={{
          display: "flex",
          gap: "10px",
          flexWrap: "wrap",
          marginBottom: "10px",
        }}
      >
        <button onClick={showSuccess}>Success (Top Right)</button>
        <button onClick={showError}>Error (Top Left)</button>
        <button onClick={showWarning}>Warning (Bottom Right)</button>
      </div>
      <div
        style={{
          display: "flex",
          gap: "10px",
          flexWrap: "wrap",
          marginBottom: "10px",
        }}
      >
        <button onClick={showInfo}>Info (Bottom Left)</button>
        <button onClick={showTop}>Info (Top Center)</button>
        <button onClick={showBottom}>Success (Bottom Center)</button>
      </div>
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
        <button onClick={changePlacement}>Change Default to Top Left</button>
        <button onClick={() => toaster.clearAll()}>Clear All</button>
      </div>
    </div>
  );
};
